import React from 'react'
import PropTypes from 'prop-types'
import { List, ListItem, ListItemText } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'

/**
 * Historia 120 (Jupiter, paquete P3) — `03-dominio.md` §7.2 #15-bis/#15-ter, §8.3/§8.3-bis, D-SP20.
 *
 * 🔒 THE ONLY reason renderer/translator in this tree. Every surface that shows a reason string
 * emitted by Sun goes through `translateBatchReason` (or one of its two thin wrappers below, or the
 * <BatchReasonsList> component, which is that same function applied for display) — there is no
 * second translator anywhere else. TWO reason renderers, or two translators for the same literal,
 * is a DECLARED DEFECT, not a style choice — exactly like `BatchBlockedNotice.jsx` is the only
 * blocked-batch renderer for the same reason (D-SP14).
 *
 * The rule is exactly `t_(motivo)`, with NO branch of any kind on the reason's content:
 *   - bare i18n keys (`TechnologyRequiredForJob`, `JobGovernedByAcceptance`, `NOT AVAILABLE`,
 *     `Check all parts have been reported`, `Check all resources are being used`,
 *     `Previous job not finished`, `ActiveOnAnotherDevice`, `JobNotInDeviceQueue`, and the three
 *     fourth-gate literals — `Worker does not have the technology required by this job`,
 *     `Worker is not allowed to prepare this job`, `Worker is not allowed to execute this job`)
 *     translate normally, because they have an entry in the 8 locale files;
 *   - the external-circuit literal (`Job '<label>': external job actions are governed by the
 *     external jobs widget`) has NO dictionary entry anywhere, so `t_` returns it unchanged and it
 *     renders verbatim — that is `t_`'s EXISTING behaviour (i18next: missing key -> the key
 *     itself), not a special case coded here.
 * 🚫 FORBIDDEN, each one a defect: an `if` to "detect" whether a reason is translatable; splitting
 * the array into two lists; adding an i18n entry for the external-circuit literal.
 *
 * ⚠️ ENMIENDA 03 (D-SP20): SIX surfaces share this module, not four. The four `reasons[]`/
 * `notStarted[].reasons` surfaces — (1) start preview (N1 `jobs[].reasons`), (2) finish preview
 * (N4 `jobs[].reasons`), (3) the `BLOCKED` of N3/N5 (`jobs[].reasons`, rendered by
 * `BatchBlockedNotice.jsx` via the SAME <BatchReasonsList> below), (4) the batch-start RESULT
 * (N2 `notStarted[].reasons`) — plus TWO that are not `reasons[]` and carry the SAME literals:
 * (5) the `400 MessageDto{message}` of N2/N3/N5, translated with `translateBatchRejectionMessage`
 * — itself a straight call to `translateBatchReason`, with NO whitelist of "expected" literals
 * (§7.2 #15-ter: a whitelist here is exactly the defect B3 flagged) — and (6) the `warnings[]` of
 * N2 (E-B7, `BatchRecordNotPersisted`), translated with `translateBatchReasons`, the SAME list
 * function `reasons[]` uses. One translator, two transports; a renderer/translator per transport
 * is the declared defect.
 */

// The atomic translation step every surface funnels through. No whitelist, no branch on content --
// whatever `t_` does with a missing key (return it verbatim) IS the external-circuit behaviour.
export const translateBatchReason = (t_, reason) => t_(reason)

// Surfaces (1)-(4) and (6): `reasons[]` and `warnings[]` are both flat string arrays carrying the
// exact same kind of literal (§8.3-bis) -- one list function serves both, there is no
// reason-specific vs warning-specific variant.
export const translateBatchReasons = (t_, reasons) =>
  (Array.isArray(reasons) ? reasons : []).map((reason) => translateBatchReason(t_, reason))

// Surface (5): the 400 `MessageDto{message}` of N2/N3/N5. `Api_Functions.jsx` already resolves the
// rejected body down to a single string (`data.message`, or `data.join('. ')` when the body arrives
// as a list) -- this is a one-line wrapper so nobody re-implements a second translator, with or
// without a literal whitelist, for the very same reasons (§7.2 #15-ter).
export const translateBatchRejectionMessage = (t_, error) => translateBatchReason(t_, error?.message ?? '')

/**
 * The rendering half of the same rule: a plain list, one row per reason, each row `t_(motivo)`.
 * Used directly by surfaces (1), (2) and (4), and reused unchanged by `BatchBlockedNotice.jsx` for
 * surface (3) -- nobody renders a reason list of their own.
 */
const BatchReasonsList = ({ reasons }) => {
  const { t_ } = useSmartTranslation()
  const list = Array.isArray(reasons) ? reasons : []

  if (list.length === 0) {
    return null
  }

  return (
    <List dense disablePadding>
      {list.map((reason, index) => (
        <ListItem key={`${index}-${reason}`} disableGutters>
          <ListItemText primary={translateBatchReason(t_, reason)} />
        </ListItem>
      ))}
    </List>
  )
}

BatchReasonsList.propTypes = {
  reasons: PropTypes.arrayOf(PropTypes.string)
}

export default BatchReasonsList
